/**
 * auth-context.js
 *
 * Provides authentication state + subscription tier across the app.
 *
 * Dev backdoor: set REACT_APP_DEV_TIER=elite (or pro/free) in .env.local
 * to bypass Firestore tier lookup — useful for testing gated pages locally.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, googleProvider, microsoftProvider } from '../services/firebase-config';
import { getServerApiBase } from '../services/api-config';
import { markOnboardingSeen, markCoinDetailOnboardingSeen } from '../services/onboarding-service';

const AuthContext = createContext(null);

// Dev tier override — only active in development builds
const DEV_TIER = process.env.REACT_APP_DEV_TIER || null;

// Fire-and-forget — adds a brand-new user's email to the Kit audience
// via the server (server/kit-service.js). Never blocks sign-in; no-ops
// outside VPS mode or if the server-side Kit env vars aren't set.
function subscribeToMailingList(email) {
  if (!email) return;
  const base = getServerApiBase();
  if (base === null) return;
  fetch(`${base}/api/subscribe`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ email }),
  }).catch(err => console.warn('[Auth] Mailing list subscribe failed:', err.message));
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [tier, setTier]       = useState('free');
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showCoinDetailOnboarding, setShowCoinDetailOnboarding] = useState(false);

  // Fetch / create the user's tier doc in Firestore
  async function syncUserTier(firebaseUser) {
    if (!firebaseUser) {
      setTier('free');
      return;
    }

    // Dev backdoor — skip Firestore in local dev when override is set
    if (DEV_TIER) {
      console.info('[Auth] DEV_TIER override active:', DEV_TIER);
      setTier(DEV_TIER);
      return;
    }

    try {
      const ref  = doc(db, 'users', firebaseUser.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setTier(snap.data().tier || 'free');
        // Missing field (accounts created before a given tour shipped)
        // counts as "not yet seen" — every existing user gets each tour
        // once too, not just future signups. Only an explicit `true`
        // (written once shown/skipped) suppresses it.
        setShowOnboarding(snap.data().hasSeenOnboarding !== true);
        setShowCoinDetailOnboarding(snap.data().hasSeenCoinDetailOnboarding !== true);
      } else {
        // New user — create free tier document
        await setDoc(ref, {
          uid:              firebaseUser.uid,
          email:            firebaseUser.email || '',
          tier:             'free',
          hasSeenOnboarding: false,
          hasSeenCoinDetailOnboarding: false,
          createdAt:        new Date().toISOString(),
        });
        setTier('free');
        setShowOnboarding(true);
        setShowCoinDetailOnboarding(true);
        subscribeToMailingList(firebaseUser.email);
      }
    } catch (err) {
      console.warn('[Auth] Could not read Firestore tier:', err.message);
      setTier('free');
    }
  }

  // Called once the Dashboard tour finishes or is skipped — optimistically
  // hides it immediately and persists the flag so it never shows again.
  async function dismissOnboarding() {
    setShowOnboarding(false);
    if (user) {
      try {
        await markOnboardingSeen(user.uid);
      } catch (err) {
        console.warn('[Auth] Could not persist onboarding dismissal:', err.message);
      }
    }
  }

  // Same as dismissOnboarding, for the separate Coin Detail page tour.
  async function dismissCoinDetailOnboarding() {
    setShowCoinDetailOnboarding(false);
    if (user) {
      try {
        await markCoinDetailOnboardingSeen(user.uid);
      } catch (err) {
        console.warn('[Auth] Could not persist coin detail onboarding dismissal:', err.message);
      }
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      await syncUserTier(firebaseUser);
      setIsLoading(false);
    });
    return unsubscribe;
  }, []);

  async function signInWithGoogle() {
    setAuthError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }

  async function signInWithMicrosoft() {
    setAuthError(null);
    try {
      await signInWithPopup(auth, microsoftProvider);
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }

  async function signInWithEmail(email, password) {
    setAuthError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }

  async function signUpWithEmail(email, password) {
    setAuthError(null);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setAuthError(err.message);
      throw err;
    }
  }

  async function signOut() {
    await firebaseSignOut(auth);
    setUser(null);
    setTier('free');
    setShowOnboarding(false);
    setShowCoinDetailOnboarding(false);
  }

  // Dev-only: flip the current user's tier for testing, without a Stripe charge.
  // Firestore rules correctly block clients from writing their own `tier` field,
  // so this calls a server endpoint (server/index.js: /api/dev-set-tier) that
  // verifies the ID token server-side and writes via the Admin SDK. The server
  // independently checks the token's email against DEV_ADMIN_EMAIL — the UI
  // gating (REACT_APP_DEV_ADMIN_EMAIL) is just to hide the button, not security.
  async function setTierOverride(newTier) {
    if (!user) return;
    const idToken = await user.getIdToken();
    const base = getServerApiBase();
    if (base === null) throw new Error('Dev tier override requires REACT_APP_API_MODE=vps');
    const res = await fetch(`${base}/api/dev-set-tier`, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        Authorization:   `Bearer ${idToken}`,
      },
      body: JSON.stringify({ tier: newTier }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || `Server error ${res.status}`);
    setTier(newTier);
  }

  return (
    <AuthContext.Provider value={{
      user,
      tier,
      isLoading,
      authError,
      showOnboarding,
      dismissOnboarding,
      showCoinDetailOnboarding,
      dismissCoinDetailOnboarding,
      signInWithGoogle,
      signInWithMicrosoft,
      signInWithEmail,
      signUpWithEmail,
      signOut,
      setTierOverride,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
